/**
 * ViewerModeToggle — mode selector for the plan viewer toolbar.
 *
 * Desktop: icon ButtonGroup (one button per available mode).
 * Mobile: a Select dropdown with icon + label options (both visible for now).
 *
 * Reads available modes and active mode from ProductContext.
 * Returns null when only one mode is available (no switching needed).
 */

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { useProduct } from '../../index.js'
import { PRODUCT_VIEW_MODE_ICONS } from '../../constants/viewModes.js'

import { Button, ButtonGroup } from '../../../../shared/ui/Button/index.js'
import { Select }              from '../../../../shared/ui/Select/index.js'

import styles from './ViewerModeToggle.module.scss'

// ── Option label: icon + text ──────────────────────────────────────────────────

function ModeOptionLabel({ icon: Icon, label }) {
  return (
    <div className={styles.modeOptionLabel}>
      {Icon && <Icon width="1.5em" height="1.5em" />}
      {label}
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ViewerModeToggle({ 
    isMobile 
  }) {
  const { t } = useI18n()
  const { modes } = useProduct()
  const { availableModes, activeMode, setActiveMode } = modes

  if (!availableModes || availableModes.length <= 1) return null

  const selectOptions = availableModes.map((mode) => {
    const Icon = PRODUCT_VIEW_MODE_ICONS[mode.id] ?? null
    return {
      value: mode.id,
      label: <ModeOptionLabel icon={Icon} label={t(mode.labelKey)} />,
    }
  })

  return (
    <div className={styles.modeToggleBox}>

      {/* MOBILE: Mode select with icon + label options */}
      {isMobile && (
        <>
          <span className={styles.label}>
            {t('product.viewer_mode_label')}
          </span>
          <Select
            value={activeMode}
            onChange={setActiveMode}
            options={selectOptions}
            className={styles.modeSelect}
          />
        </>
      )}

      {/* DESKTOP: Icon button group — sliding indicator shows active mode */}
      {!isMobile && (
        <ButtonGroup
          sliding
          size="large"
          aria-label={t('product.viewer_mode_label')}
          style={{ width: `calc(${availableModes.length} * var(--_button-group-track-size) + 2px)`, flexShrink: 0 }}
        >
        {availableModes.map((mode) => {
          const Icon = PRODUCT_VIEW_MODE_ICONS[mode.id] ?? null

          return (
            <Button
              key={mode.id}
              type="button"
              kind="icon"
              size="large"
              selected={mode.id === activeMode}
              onClick={() => !mode.isDisabled && setActiveMode(mode.id)}
              disabled={mode.isDisabled}
              aria-pressed={mode.id === activeMode}
              title={t(mode.labelKey)}
            >
              {Icon && <Icon width="1em" height="1em" />}
            </Button>
          )
        })}
      </ButtonGroup>
      )}
    </div>
  )
}
